/**
 * Roster completo de personajes de ZAMBAZOKA. Datos puros desde
 * `docs/biblia/personajes.md`: apodos exactos, rasgos de caricatura,
 * colores hex y emojis representativos. La beta no usa assets binarios:
 * el motor dibuja el retrato por código (círculo de color + emoji +
 * apodo) usando `Character.color` y `Character.emoji`.
 *
 * Reglas duras:
 * - Apodos en MAYÚSCULAS como en la biblia (incluida la tilde de YASSAFRÁN).
 * - Cada color debe ser visualmente distinguible del resto (se ven en
 *   paralelo en el retrato de un diálogo con varios hablantes).
 * - Solo SDOWP es `jugable-beta`; el resto del grupo es `aliado`; los
 *   tres del barrio (paki, promo, amigo cigarro) son `pnj`.
 */

import type { Character } from './types';

/** Roster completo exportado: 1 jugable-beta + 12 aliados + 3 pnj = 16. */
export const personajes: Character[] = [
  {
    id: 'sdowp',
    apodo: 'SDOWP',
    rol: 'jugable-beta',
    color: 0x4a90e2, // azul — el pegamento del grupo
    emoji: '🛋️', // el del sofá de la casa
    retratos: { neutral: 'code' },
    rasgos: [
      'bastante delgadillo',
      'siempre está en la casa',
      'el pegamento del grupo',
      'convoca con el sticker de WhatsApp',
    ],
    especialidadMinijuego: 'sticker-reunion',
  },
  {
    id: 'xano',
    apodo: 'XANO',
    rol: 'aliado',
    color: 0x8e44ad, // púrpura — oráculo/IA
    emoji: '🧠',
    retratos: { neutral: 'code' },
    rasgos: [
      'sube poco a la casa pero muy querido',
      '1,80 m, ~80 kg',
      'trading e inteligencia artificial',
      'te cuenta cosas que no sabías',
    ],
    especialidadMinijuego: 'oraculo-info',
  },
  {
    id: 'banagrayn',
    apodo: 'BANAGRAYN',
    rol: 'aliado',
    color: 0x27ae60, // verde bosque — forestal
    emoji: '🌲',
    retratos: { neutral: 'code' },
    rasgos: [
      'forestal, excelente amante del bosque',
      'introvertido pero muy gracioso',
      'está muy fuerte',
      '1,76 m, ~70 kg',
    ],
    especialidadMinijuego: 'monte-atajos',
  },
  {
    id: 'gordako',
    apodo: 'GORDAKO',
    rol: 'aliado',
    color: 0xf39c12, // naranja — tecnología cálida
    emoji: '💻',
    retratos: { neutral: 'code' },
    rasgos: [
      'autónomo',
      'sabe mucho de tecnología',
      'vive en Barcelona',
      'resuelve por pantalla desde BCN',
    ],
    especialidadMinijuego: 'soporte-remoto',
  },
  {
    id: 'yassafran',
    apodo: 'YASSAFRÁN',
    rol: 'aliado',
    color: 0xe74c3c, // rojo — urgencias/adrenalina
    emoji: '⛑️',
    retratos: { neutral: 'code' },
    rasgos: [
      'árabe, habla perfectamente español',
      'muy fuerte',
      'trabaja de ambulanciero',
      'le encanta la montaña y el deporte de riesgo',
    ],
    especialidadMinijuego: 'ambulanciero-curar',
  },
  {
    id: 'gerardbu',
    apodo: 'GERARDBU',
    rol: 'aliado',
    color: 0xc0392b, // rojo oscuro — bombero directo
    emoji: '🚒',
    retratos: { neutral: 'code' },
    rasgos: [
      'bombero',
      'bastante directo, sin rodeos',
      'muy fuerte',
      'le encantan las actividades de montaña',
      'hermano de ALEIXBU',
    ],
    especialidadMinijuego: 'fuerza-bruta',
  },
  {
    id: 'joares-sossa',
    apodo: 'JOARES SOSSA',
    rol: 'aliado',
    color: 0x16a085, // teal — observador con dron
    emoji: '🛸',
    retratos: { neutral: 'code' },
    rasgos: [
      'profesor',
      'bueno con drones',
      'gracioso pero duro y serio',
      'mezcla rara de chiste y veracidad',
    ],
    especialidadMinijuego: 'dron-reconocimiento',
  },
  {
    id: 'sempai',
    apodo: 'SEMPAI',
    rol: 'aliado',
    color: 0x9b59b6, // violeta — vicios nocturnos
    emoji: '🎲',
    retratos: { neutral: 'code' },
    rasgos: [
      'bastante tímido',
      'muy trabajador',
      'le gusta mucho el casino',
      'fuma',
    ],
    especialidadMinijuego: 'casino-doblar',
  },
  {
    id: 'moai',
    apodo: 'MOAI',
    rol: 'aliado',
    color: 0x795548, // marrón — agricultura
    emoji: '🌾',
    retratos: { neutral: 'code' },
    rasgos: [
      'reparte productos agrícolas',
      'siempre a las seis fichando en la casa',
      'muy majo pero insulta a todo el mundo',
      'insultos con cariño',
    ],
    especialidadMinijuego: 'proveedor-diario',
  },
  {
    id: 'lede',
    apodo: 'LEDE',
    rol: 'aliado',
    color: 0xf1c40f, // amarillo — crack, estrella
    emoji: '⭐',
    retratos: { neutral: 'code' },
    rasgos: [
      'un crack en todo lo que hace',
      'flow gitanillo',
      'comodín del grupo',
    ],
    especialidadMinijuego: 'comodin-universal',
  },
  {
    id: 'young-emo',
    apodo: 'YOUNG EMO',
    rol: 'aliado',
    color: 0xad1457, // magenta oscuro — metal
    emoji: '🎸',
    retratos: { neutral: 'code' },
    rasgos: [
      'toca en un grupo de heavy metal',
      'le encantan los videojuegos',
      'siempre dispuesto a charlar',
      'nunca te deja con la palabra en la boca',
    ],
    especialidadMinijuego: 'ritmo-oid-musico',
  },
  {
    id: 'aleixbu',
    apodo: 'ALEIXBU',
    rol: 'aliado',
    color: 0xff5722, // naranja intenso — cocina/fuego
    emoji: '🍳',
    retratos: { neutral: 'code' },
    rasgos: [
      'cocinero de alta gama',
      'sabe hacer varias cosas más',
      'loco de la música hardcore',
      'loco de la fiesta',
      'hermano de GERARDBU',
    ],
    especialidadMinijuego: 'cocinero-buffs',
  },
  {
    id: 'axeliki',
    apodo: 'AXELIKI',
    rol: 'aliado',
    color: 0x455a64, // gris azulado — BMW/calle
    emoji: '🚗',
    retratos: { neutral: 'code' },
    rasgos: [
      'da vueltas por el barrio con su BMW vendiendo marihuana',
      'tiene un estudio de tatuaje',
      'siempre va tarde',
      '"5 minutos y estoy" — y no está',
    ],
    especialidadMinijuego: 'tienda-yerba',
  },
  {
    id: 'el-del-paki',
    apodo: 'EL DEL PAKI',
    rol: 'pnj',
    color: 0x8bc34a, // verde lima — tienda comida
    emoji: '🏪',
    retratos: { neutral: 'code' },
    rasgos: [
      'regenta la tienda de comida del barrio',
      'tiendo funcional',
    ],
    especialidadMinijuego: 'tienda-comida',
  },
  {
    id: 'el-promo',
    apodo: 'EL PROMO',
    rol: 'pnj',
    color: 0x00bcd4, // cian brillante — patinete
    emoji: '🛴',
    retratos: { neutral: 'code' },
    rasgos: [
      'va en patinete por el barrio',
      'te persigue pidiéndote cigarros',
      'cigarros cueste lo que cueste',
    ],
    especialidadMinijuego: 'persecucion-barrio',
  },
  {
    id: 'el-amigo-cigarro',
    apodo: 'EL AMIGO CIGARRO',
    rol: 'pnj',
    color: 0xbcaaa4, // beige humo — cigarros
    emoji: '🚬',
    retratos: { neutral: 'code' },
    rasgos: [
      'segundo perseguidor del barrio',
      'siempre pidiendo el cigarro',
      'forma pareja con EL PROMO',
    ],
    especialidadMinijuego: 'persecucion-barrio',
  },
];

/**
 * Devuelve el Character con ese id. Lanza error con la lista de ids
 * disponibles si no existe — así un id mal escrito en una Mission se
 * detecta en consola al lanzar la escena, no en un fallo silencioso.
 */
export function getCharacter(id: string): Character {
  const c = personajes.find((p) => p.id === id);
  if (!c) {
    const disponibles = personajes.map((p) => p.id).join(', ');
    throw new Error(
      `[personajes] Personaje con id "${id}" no encontrado. Ids disponibles: ${disponibles}`,
    );
  }
  return c;
}